import { useState } from 'react'; 
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Trash2, ChevronRight, CreditCard, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

import { NavbarPublic } from '../../components/NavbarPublic.jsx';
import { formatCurrency } from '../../lib/format.ts';
import { useCarrito } from '../../features/carrito/useCarrito.ts';
import { usePublicStorePricingRate, formatDualPrice } from '../../lib/dolar.ts';

// --- FONDO AMBIENTAL ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#020202]">
    <div className="absolute top-[20%] left-[-10%] w-[60vw] h-[60vw] bg-indigo-900/20 rounded-full blur-[150px] opacity-30 mix-blend-screen" />
    <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-purple-900/10 rounded-full blur-[150px] opacity-20 mix-blend-screen" />
  </div>
);

export function Carrito() {
  const carrito = useCarrito();
  const { slug } = useParams();
  const { data: pricingRate } = usePublicStorePricingRate(slug);
  const navigate = useNavigate();

  // --- FILTRO CRÍTICO: SOLO ITEMS DE ESTA TIENDA ---
  const currentStoreItems = carrito.items.filter(
      item => item.storeSlug === slug || !item.storeSlug
  );

  const total = currentStoreItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const dualTotal = pricingRate ? formatDualPrice(total, pricingRate) : null;

  // Función para actualizar cantidad de forma segura
  const handleUpdateQuantity = (itemId, newQty) => {
    let qty = parseInt(newQty, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    
    // Pasamos el slug para asegurar que actualizamos el item de ESTA tienda
    carrito.updateQuantity(itemId, qty, slug);
  };

  const handleRemoveItem = (itemId) => {
      // Pasamos el slug para borrar el item correcto
      carrito.removeItem(itemId, slug);
  };

  return (
    <div className="min-h-screen text-slate-200 font-sans selection:bg-indigo-500/30 flex flex-col">
      <AmbientBackground />
      <NavbarPublic />
      
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 pt-32 pb-12 relative z-10">
        
        {/* Header con Botón Volver */}
        <div className="flex items-center justify-between mb-8">
            <button
            onClick={() => navigate(`/t/${slug}`)}
            className="group inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors"
            >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Volver a la Tienda
            </button>
            
            <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                <ShoppingBag className="h-6 w-6 text-indigo-400" />
                Tu Carrito
            </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
            
            {/* LISTA DE PRODUCTOS */}
            <div className="lg:col-span-2 space-y-4">
            {currentStoreItems.length === 0 ? (
                <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/60 backdrop-blur-xl p-12 text-center flex flex-col items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
                        <ShoppingBag className="h-8 w-8 text-white/20" />
                    </div>
                    <p className="text-lg font-medium text-white mb-2">Tu carrito está vacío</p>
                    <p className="text-white/40 text-sm mb-8 max-w-xs">
                        Explora nuestro catálogo y encuentra productos increíbles.
                    </p>
                    <Link
                        to={`/t/${slug}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors"
                    >
                        Ver Productos <ChevronRight className="h-3 w-3" />
                    </Link>
                </div>
            ) : (
                currentStoreItems.map((item) => (
                <div
                    key={item.id}
                    className="group relative overflow-hidden rounded-2xl border border-white/5 bg-[#0A0A0A]/80 p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-white/10 transition-all"
                >
                    {/* Imagen e Info Básica */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border border-white/10 bg-black">
                            <img
                                src={item.image ?? 'https://placehold.co/96x96?text=Producto'}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(event) => { event.currentTarget.src = 'https://placehold.co/96x96?text=Producto'; }}
                            />
                        </div>

                        <div className="min-w-0 flex-1">
                            {/* Usamos line-clamp-2 para permitir 2 líneas y evitar overflow */}
                            <h2 
                                className="text-sm font-bold text-white line-clamp-2 pr-8 leading-snug"
                                title={item.name}
                            >
                                {item.name}
                            </h2>
                            
                            {item.variant_attributes && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {Object.entries(item.variant_attributes).map(([key, val]) => (
                                        <span key={key} className="text-[9px] font-mono text-white/50 uppercase bg-white/5 px-1.5 py-0.5 rounded border border-white/5">
                                            {key}: {val}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Controles y Precio (Flex-shrink-0 impide que se aplasten) */}
                    <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto mt-4 sm:mt-0 border-t sm:border-t-0 border-white/5 pt-4 sm:pt-0 sm:flex-shrink-0 sm:ml-4">
                         
                         {/* CONTROL DE CANTIDAD */}
                         <div className="flex items-center bg-[#151515] rounded-lg border border-white/10 p-0.5 h-9 flex-shrink-0">
                            <button 
                               onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                               className="w-8 h-full flex items-center justify-center rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                            >
                               <Minus className="h-3 w-3" />
                            </button>
                            
                            <input 
                               type="number"
                               min="1"
                               value={item.quantity}
                               onChange={(e) => handleUpdateQuantity(item.id, e.target.value)}
                               className="w-10 bg-transparent border-none text-center font-mono text-sm text-white focus:ring-0 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />

                            <button 
                               onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                               className="w-8 h-full flex items-center justify-center rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                            >
                               <Plus className="h-3 w-3" />
                            </button>
                         </div>
                         
                         {/* Precios */}
                         <div className="text-right min-w-[80px] flex-shrink-0">
                            <p className="text-sm font-medium text-white tracking-wide">
                                {formatCurrency(item.price * item.quantity)}
                            </p>
                            {pricingRate && (
                                <p className="text-[10px] font-mono text-emerald-400 mt-0.5">
                                    {formatDualPrice(item.price * item.quantity, pricingRate).local}
                                </p>
                            )}
                         </div>
                    </div>

                    {/* Botón Eliminar */}
                    <button
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        className="absolute top-3 right-3 p-2 text-white/20 hover:text-rose-500 transition-colors sm:static sm:p-0 sm:ml-2"
                        title="Eliminar producto"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
                ))
            )}
            </div>

            {/* RESUMEN DE COMPRA */}
            {currentStoreItems.length > 0 && (
                <div className="lg:col-span-1">
                    <div className="sticky top-32 space-y-6">
                        
                        <div className="rounded-[2rem] border border-white/10 bg-[#0F0F0F]/80 backdrop-blur-xl p-6 shadow-2xl">
                            <h2 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-6 border-b border-white/5 pb-4">
                                Resumen de Orden
                            </h2>

                            <div className="flex justify-between items-end mb-2">
                                <span className="text-sm text-white/70">Subtotal</span>
                                <span className="text-xl font-light text-white">{formatCurrency(total)}</span>
                            </div>

                            {dualTotal && (
                                <div className="flex justify-between items-center mb-6">
                                    <span className="text-xs text-white/30 font-mono">Aprox. en Moneda Local</span>
                                    <span className="text-sm font-mono text-emerald-400">{dualTotal.local}</span>
                                </div>
                            )}

                            <Link
                                to={`/t/${slug}/checkout`}
                                className="group w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold uppercase tracking-widest text-xs shadow-[0_0_20px_rgba(99,102,241,0.3)] transition-all transform hover:scale-[1.02]"
                            >
                                <CreditCard className="h-4 w-4" />
                                Proceder al Pago
                            </Link>

                            <p className="text-[10px] text-center text-white/20 mt-4 leading-relaxed">
                                Los impuestos y costos de envío se calculan en el siguiente paso.
                            </p>
                        </div>

                        <div className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/20">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                            Checkout Seguro 256-bit SSL
                        </div>

                    </div>
                </div>
            )}

        </div>

        {/* Footer con Términos y Condiciones */}
        <footer className="mt-16 border-t border-white/5 pt-8 pb-6">
          <div className="text-center space-y-3">
            <p className="text-xs text-white/40">
              © 2026 Tienda. Todos los derechos reservados.
            </p>
            <div className="flex items-center justify-center gap-4 text-xs">
              <a
                href="/terminos-clientes"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-emerald-400 transition-colors underline"
              >
                Términos y Condiciones
              </a>
              <span className="text-white/20">•</span>
              <span className="text-white/30">
                Powered by <span className="text-emerald-400 font-semibold">TiendasYA</span>
              </span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}